import { requestAPI } from '../../../../utils/request';
import {
  sharepointSettingsRequest,
  sharepointSettingsSuccess,
  sharepointSettingsFailure,
  sharepointSitesRequest,
  sharepointSitesSuccess,
  sharepointSitesFailure,
  sharepointDrivesRequest,
  sharepointDrivesSuccess,
  sharepointDrivesFailure,
  sharepointSaveRequest,
  sharepointSaveSuccess,
  sharepointSaveFailure,
  setSelectedSite,
} from './actions';
import message from '../../../../utils/message';

const CLIENT_ID = process.env.VITE_SHAREPOINT_CLIENT_ID || '';

export const fetchSharePointSettings =
  (uploadType?: string) => (dispatch: any) => {
    dispatch(sharepointSettingsRequest());
    return requestAPI(
      '/fleets/admin/sharepoint-settings',
      uploadType ? { uploadType } : {}
    )
      .then((data) => {
        dispatch(sharepointSettingsSuccess(data));
      })
      .catch((err) => {
        dispatch(sharepointSettingsFailure(err));
        message.error(err.message);
      });
  };

export const fetchSharePointSites = (query: string) => (dispatch: any) => {
  const token = sessionStorage.getItem('sp_access_token');
  if (!token) {
    dispatch(
      sharepointSitesFailure({
        name: 'AuthError',
        message: 'No access token found. Please re-authorize.',
      })
    );
    return;
  }

  dispatch(sharepointSitesRequest());
  return fetch(
    `https://graph.microsoft.com/v1.0/sites?search=${encodeURIComponent(
      query.trim()
    )}`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  )
    .then((res) => res.json())
    .then((data) => {
      if (data.error) throw new Error(data.error.message);
      dispatch(sharepointSitesSuccess(data.value || []));
    })
    .catch((err) => {
      dispatch(sharepointSitesFailure(err));
      message.error(err.message);
    });
};

export const lookupSharePointSiteByUrl =
  (siteUrl: string) => (dispatch: any) => {
    const token = sessionStorage.getItem('sp_access_token');
    if (!token) {
      dispatch(
        sharepointSitesFailure({
          name: 'AuthError',
          message: 'No access token found. Please re-authorize.',
        })
      );
      return;
    }

    dispatch(sharepointSitesRequest());

    try {
      const parsed = new URL(siteUrl.trim());
      const hostname = parsed.hostname;
      const sitePath = parsed.pathname
        .replace(/\/SitePages.*$/, '')
        .replace(/\/$/, '');

      return fetch(
        `https://graph.microsoft.com/v1.0/sites/${hostname}:${sitePath}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.error) throw new Error(data.error.message);
          dispatch(sharepointSitesSuccess([data]));
          dispatch(setSelectedSite(data));
        })
        .catch((err) => {
          dispatch(sharepointSitesFailure(err));
          message.error(err.message);
        });
    } catch (err: any) {
      dispatch(sharepointSitesFailure(err));
      message.error(err.message || 'Invalid URL or site not found.');
    }
  };

export const fetchSharePointDrives = (siteId: string) => (dispatch: any) => {
  const token = sessionStorage.getItem('sp_access_token');
  if (!token) {
    dispatch(
      sharepointDrivesFailure({
        name: 'AuthError',
        message: 'Missing access token.',
      })
    );
    return;
  }

  dispatch(sharepointDrivesRequest());
  return fetch(`https://graph.microsoft.com/v1.0/sites/${siteId}/drives`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => res.json())
    .then((data) => {
      if (data.error) throw new Error(data.error.message);
      dispatch(sharepointDrivesSuccess(data.value || []));
    })
    .catch((err) => {
      dispatch(sharepointDrivesFailure(err));
      message.error(err.message);
    });
};

export const grantPermissionAndSave =
  (params: {
    selectedSite: any;
    selectedDrive: any;
    folderPath: string;
    tenantId: string | null;
    uploadType: string;
  }) =>
  (dispatch: any) => {
    const { selectedSite, selectedDrive, folderPath, tenantId, uploadType } =
      params;
    const token = sessionStorage.getItem('sp_access_token');
    if (!token) {
      dispatch(
        sharepointSaveFailure({
          name: 'AuthError',
          message: 'Missing access token.',
        })
      );
      return;
    }

    dispatch(sharepointSaveRequest());

    // Step 1: Grant the GETAWAY app permission on the selected site
    let permissionGranted = false;

    const grantPermission = fetch(
      `https://graph.microsoft.com/v1.0/sites/${selectedSite.id}/permissions`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          roles: ['write'],
          grantedToIdentities: [
            {
              application: {
                id: CLIENT_ID,
                displayName: 'GETAWAY SharePoint Integration',
              },
            },
          ],
        }),
      }
    )
      .then((res) => res.json())
      .then((permData) => {
        if (permData.error) {
          console.warn(
            '[SharePoint] Permission grant failed (may need manual setup):',
            permData
          );
        } else {
          permissionGranted = true;
        }
      })
      .catch((permErr) => {
        console.warn(
          '[SharePoint] Permission grant request failed (may need manual setup):',
          permErr
        );
      });

    // After permission attempt, save settings to backend
    return grantPermission
      .then(() => {
        const siteParts = selectedSite.id.split(',');
        const siteHost = siteParts[0] || '';
        const siteGuid =
          siteParts.length >= 3
            ? siteParts[2]
            : siteParts[siteParts.length - 1];

        return requestAPI(
          '/fleets/admin/sharepoint-settings',
          { uploadType },
          {
            method: 'PUT',
            body: JSON.stringify({
              siteId: siteGuid,
              siteHost,
              tenantId,
              siteUrl: selectedSite.webUrl,
              libraryId: selectedDrive.id,
              driveId: selectedDrive.id,
              path: folderPath || undefined,
              appClientId: CLIENT_ID,
            }),
          }
        );
      })
      .then((backendResult) => {
        dispatch(
          sharepointSaveSuccess({
            ...backendResult,
            permissionGranted,
            site: selectedSite,
          })
        );
      })
      .catch((err) => {
        dispatch(sharepointSaveFailure(err));
        message.error(err.message || 'Failed to complete SharePoint setup.');
      });
  };
